import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { toast } from 'react-toastify';
import ContentLayout from '../../../layout/ContentLayout';
import { Container } from '../../../components/content';
import DeleteModal from '../../../components/modals/DeleteModal';
import ToggleSwitch from '../../../components/elements/toggleSwitch/ToggleSwitch';
import ProductForm from '../../../components/products/ProductForm';
import {
  fetchProductById,
  updateProduct,
  fetchProductFilters,
  hideProduct,
  restoreProduct,
  updateProductStockStatus,
  fetchProductCommissionChain,
  saveProductCommissionChain,
  deleteProductCommissionChain,
  fetchProductAgentCommission,
  saveProductAgentCommission,
  deleteProductAgentCommission,
  fetchProductCashbackOverride,
  saveProductCashbackOverride,
  deleteProductCashbackOverride,
  fetchCurrentCheckoutUpsellProduct,
  updateProductCheckoutUpsell,
} from '../../../api/webshop-products-orders-api';
import styles from '../../../styles/mobileInfoPage.module.scss';

const emptyLangContent = {
  name: '',
  description: '',
  short_description: '',
  helpsWithMetaDataSection: '',
  customFieldMetaDataSection: { title: '', htmlContent: '' },
};

const emptyCommissionChain = {
  enabled: false,
  maxPercent: '',
  level1Percent: '',
  level2Percent: '',
  level3Percent: '',
  level4Percent: '',
};

const emptyAgentCommission = {
  enabled: false,
  percent: '',
};

const emptyCashbackOverride = {
  hasOverride: false,
  enabled: true,
  mode: 'points',
  percent: '',
};

const mapProductToForm = (data, commissionChain, agentCommission, cashbackOverride) => {
  const ml = data.multiLanguage || {};
  return {
    slug: data.slug || '',
    sku: data.sku || '',
    price: data.price ?? '',
    regular_price: data.regular_price ?? '',
    sale_price: data.sale_price ?? '',
    vip_price: data.vip_price ?? '',
    menu_order: data.menu_order ?? '',
    status: data.status || 'publish',
    isConfigurationProduct: !!data.isConfigurationProduct,
    isMix4IndividuallySellable: !!data.isMix4IndividuallySellable,
    hasQuantityDiscount: !!data.hasQuantityDiscount,
    isEligibleToBuyWithPoints: data.isEligibleToBuyWithPoints !== false,
    isProductHidden: !!data.isProductHidden,
    isExternalProduct: !!data.isExternalProduct,
    isSubscriptionOnlyProduct: !!data.isSubscriptionOnlyProduct,
    isFrozenProduct: !!data.isFrozenProduct,
    skipReferredDiscount: !!data.skipReferredDiscount,
    categories: data.categories || [],
    images: data.images || [],
    multiLanguage: {
      sr: { ...emptyLangContent, ...ml.sr },
      hu: { ...emptyLangContent, ...ml.hu },
      en: { ...emptyLangContent, ...ml.en },
    },
    mentorCommissionChain: commissionChain
      ? {
          enabled: true,
          maxPercent: commissionChain.maxPercent ?? '',
          level1Percent: commissionChain.level1Percent ?? '',
          level2Percent: commissionChain.level2Percent ?? '',
          level3Percent: commissionChain.level3Percent ?? '',
          level4Percent: commissionChain.level4Percent ?? '',
        }
      : { ...emptyCommissionChain },
    agentCommission: agentCommission
      ? { enabled: true, percent: agentCommission.percent ?? '' }
      : { ...emptyAgentCommission },
    cashbackOverride: cashbackOverride
      ? {
          hasOverride: true,
          enabled: cashbackOverride.enabled !== false,
          mode: cashbackOverride.mode || 'points',
          percent: cashbackOverride.percent ?? '',
        }
      : { ...emptyCashbackOverride },
    checkoutUpsell: {
      enabled: !!data.isCheckoutUpsell,
      discountPercent: data.checkoutUpsellDiscountPercent ?? '',
    },
  };
};

export default function IzmeniProizvod() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [initialForm, setInitialForm] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stockUpdating, setStockUpdating] = useState(false);
  const [hideModalOpen, setHideModalOpen] = useState(false);
  const [hiding, setHiding] = useState(false);
  const [upsellConflictOpen, setUpsellConflictOpen] = useState(false);
  const [upsellConflictProductName, setUpsellConflictProductName] = useState('');
  const [pendingSubmitArgs, setPendingSubmitArgs] = useState(null);

  const loadProduct = useCallback(async () => {
    setLoading(true);
    const [{ data, success, message }, commissionChainResult, agentCommissionResult, cashbackOverrideResult] = await Promise.all([
      fetchProductById(id),
      fetchProductCommissionChain(id),
      fetchProductAgentCommission(id),
      fetchProductCashbackOverride(id),
    ]);
    if (success && data) {
      setProduct(data);
      setInitialForm(
        mapProductToForm(
          data,
          commissionChainResult?.data ?? null,
          agentCommissionResult?.data ?? null,
          cashbackOverrideResult?.data ?? null
        )
      );
    } else {
      toast.error(message || 'Greška pri učitavanju proizvoda');
      navigate('/proizvodi');
    }
    setLoading(false);
  }, [id, navigate]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  useEffect(() => {
    fetchProductFilters().then(({ data, error }) => {
      if (!error) setCategoryOptions(data?.categories || []);
    });
  }, []);

  const performSave = async (payload, commissionChainPayload, agentCommissionPayload, checkoutUpsellPayload, cashbackOverridePayload) => {
    setSaving(true);
    const { success, message } = await updateProduct(id, payload);
    if (!success) {
      toast.error(message || 'Greška pri izmeni proizvoda');
      setSaving(false);
      return;
    }

    // No cross-collection transaction backs these writes — report each call's outcome
    // separately so a partial-success state (product saved, chain/agent/upsell not) is visible.
    const chainResult = commissionChainPayload
      ? await saveProductCommissionChain(id, commissionChainPayload)
      : await deleteProductCommissionChain(id);
    if (!chainResult.success) {
      toast.error(
        chainResult.message || 'Proizvod je izmenjen, ali provizija mentorskog lanca nije sačuvana'
      );
      setSaving(false);
      return;
    }

    const agentResult = agentCommissionPayload
      ? await saveProductAgentCommission(id, agentCommissionPayload)
      : await deleteProductAgentCommission(id);
    if (!agentResult.success) {
      toast.error(
        agentResult.message || 'Proizvod je izmenjen, ali provizija za agente nije sačuvana'
      );
      setSaving(false);
      return;
    }

    const upsellResult = await updateProductCheckoutUpsell(
      id,
      checkoutUpsellPayload.isCheckoutUpsell,
      checkoutUpsellPayload.checkoutUpsellDiscountPercent
    );
    if (!upsellResult.success) {
      toast.error(
        upsellResult.message || 'Proizvod je izmenjen, ali ponuda za dodatnu kupovinu nije sačuvana'
      );
      setSaving(false);
      return;
    }

    const cashbackResult = cashbackOverridePayload
      ? await saveProductCashbackOverride(id, cashbackOverridePayload)
      : await deleteProductCashbackOverride(id);
    if (!cashbackResult.success) {
      toast.error(
        cashbackResult.message || 'Proizvod je izmenjen, ali cashback podešavanje nije sačuvano'
      );
      setSaving(false);
      return;
    }

    toast.success('Proizvod je uspešno izmenjen');
    navigate('/proizvodi');
    setSaving(false);
  };

  const handleSubmit = async (payload, commissionChainPayload, agentCommissionPayload, checkoutUpsellPayload, cashbackOverridePayload) => {
    // Only warn when actually turning the offer on — the backend also enforces
    // exclusivity regardless (defense in depth against a stale check here).
    if (checkoutUpsellPayload.isCheckoutUpsell) {
      const { data: current } = await fetchCurrentCheckoutUpsellProduct();
      if (current && current.productId !== id) {
        setUpsellConflictProductName(current.name);
        setPendingSubmitArgs([payload, commissionChainPayload, agentCommissionPayload, checkoutUpsellPayload, cashbackOverridePayload]);
        setUpsellConflictOpen(true);
        return;
      }
    }
    await performSave(payload, commissionChainPayload, agentCommissionPayload, checkoutUpsellPayload, cashbackOverridePayload);
  };

  const handleConfirmUpsellConflict = async () => {
    setUpsellConflictOpen(false);
    if (pendingSubmitArgs) {
      await performSave(...pendingSubmitArgs);
      setPendingSubmitArgs(null);
    }
  };

  const handleStockToggle = async (e) => {
    const newStatus = e.target.checked ? 'instock' : 'outofstock';
    setStockUpdating(true);
    const { success, message } = await updateProductStockStatus(id, newStatus);
    if (success) {
      toast.success('Status zaliha je uspešno ažuriran');
      setProduct((prev) => ({ ...prev, stock_status: newStatus }));
    } else {
      toast.error(message || 'Greška pri ažuriranju statusa zaliha');
    }
    setStockUpdating(false);
  };

  const handleConfirmHide = async () => {
    setHiding(true);
    const { success, message } = await hideProduct(id);
    if (success) {
      toast.success('Proizvod je uspešno sakriven');
      setHideModalOpen(false);
      setProduct((prev) => ({ ...prev, isDeleted: true }));
    } else {
      toast.error(message || 'Greška pri sakrivanju proizvoda');
    }
    setHiding(false);
  };

  const handleRestore = async () => {
    setHiding(true);
    const { success, message } = await restoreProduct(id);
    if (success) {
      toast.success('Proizvod je uspešno vraćen');
      setProduct((prev) => ({ ...prev, isDeleted: false }));
    } else {
      toast.error(message || 'Greška pri vraćanju proizvoda');
    }
    setHiding(false);
  };

  if (loading || !initialForm) {
    return (
      <Container>
        <ContentLayout>
          <div className={styles.loadingContainer}>
            <Spin size="large" />
          </div>
        </ContentLayout>
      </Container>
    );
  }

  return (
    <Container>
      <Link to="/proizvodi" className="link_btn" style={{ marginBottom: '12px' }}>
        <img alt="back" src="/assets/icons/chevron.svg" />
        <span>Nazad</span>
      </Link>

      <ContentLayout pt={true}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '32px',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <h2 className={styles.pageTitle} style={{ margin: 0 }}>
            Izmeni proizvod
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            <ToggleSwitch
              checked={(product?.stock_status || 'instock') === 'instock'}
              onChange={stockUpdating ? undefined : handleStockToggle}
              size="md"
              label={(product?.stock_status || 'instock') === 'instock' ? 'Na zalihama' : 'Rasprodato'}
            />
            {product?.isDeleted ? (
              <Button className="btn-tertiary" onClick={handleRestore} loading={hiding}>
                Vrati proizvod
              </Button>
            ) : (
              <Button className="btn-primary red-button" onClick={() => setHideModalOpen(true)}>
                Sakrij proizvod
              </Button>
            )}
          </div>
        </div>

        <ProductForm
          mode="edit"
          initialForm={initialForm}
          categoryOptions={categoryOptions}
          saving={saving}
          onSubmit={handleSubmit}
        />
      </ContentLayout>

      <DeleteModal
        visible={hideModalOpen}
        onDelete={handleConfirmHide}
        onCancel={() => setHideModalOpen(false)}
        loading={hiding}
        title="Sakrivanje proizvoda"
        message={`Da li ste sigurni da želite da sakrijete proizvod "${product?.name}"? Proizvod će biti uklonjen iz aktivnih pretplata.`}
        buttonText="Sakrij proizvod"
        cancelText="Odustani"
      />

      <DeleteModal
        visible={upsellConflictOpen}
        onDelete={handleConfirmUpsellConflict}
        onCancel={() => {
          setUpsellConflictOpen(false);
          setPendingSubmitArgs(null);
        }}
        loading={saving}
        title="Zamena ponude za dodatnu kupovinu"
        message={`Proizvod "${upsellConflictProductName}" je trenutno podešen kao ponuda za dodatnu kupovinu na checkout-u. Ako nastavite, ta ponuda će biti uklonjena sa tog proizvoda i postavljena na "${product?.name}". Da li ste sigurni?`}
        buttonText="Da, zameni"
        cancelText="Odustani"
      />
    </Container>
  );
}
